import React, { useMemo, useCallback, type MouseEvent, type KeyboardEvent, useRef } from 'react';
import { styled } from '@compiled/react';
import AddonIcon from '@atlaskit/icon/core/migration/app--addon';
import { Section } from '@atlaskit/side-navigation';
import { getCookie } from '@atlassian/jira-browser-storage-controls/src/utils/get-cookie/index.tsx';
import { setStrictlyNecessaryCookie } from '@atlassian/jira-browser-storage-controls/src/utils/set-strictly-necessary-cookie/index.tsx';
import { useConnectItemClickHistory } from '@atlassian/jira-connect-item-click-history-controller/src/index.tsx';
import {
	withConnectHost,
	isConnectDialogWebItem,
} from '@atlassian/jira-connect-utils/src/common/utils/ecosystem-connect-host.tsx';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { NavigationContentWrapper } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/navigation-component-wrappers/index.tsx';
import { TitleHeader } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/title-header/main.tsx';
import { NestingMenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/main.tsx';
import type { NestingMenuItemProps } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/nesting-menu-item/types.tsx';
import { matchUrlExact } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import type { ConnectItemProps } from './types.tsx';

export const ConnectItem = ({
	index,
	item,
	overrides,
	showIcons,
	submenuId = '',
	isNested = false,
}: ConnectItemProps) => {
	const { addNew } = useConnectItemClickHistory();

	const isDialog = item.styleClass && isConnectDialogWebItem(item.styleClass);
	const webItemRef = useRef<HTMLElement>(null);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const BackMenuItem = {
		...overrides.BackMenuItem,
		onClick: (ev) => {
			// Due to CMP project settings not being part to the SPA, we have to set / get the state of the
			// Expandable menu items from cookies - this is because the page does a complete refresh upon clicking a menuitem link.
			// This is to satisfy a requirement for open expandable menus to remain open
			// when users return to the project settings menu
			if (submenuId != null) {
				const cookieData = getCookie(`jira.navigation.expandableMenuStates.${submenuId}`);
				let parsedCookieData = null;
				try {
					// @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
					parsedCookieData = JSON.parse(cookieData);
				} catch {
					// Rather than throw an error - just set the default state
					parsedCookieData = { submenuActive: false, expanded: false };
				}
				const newCookieData = { ...parsedCookieData, submenuActive: false };
				setStrictlyNecessaryCookie(
					`jira.navigation.expandableMenuStates.${submenuId}`,
					JSON.stringify(newCookieData),
				);
			}
			// Setting navigateOnBackMenuItemClick to true will allow backToProject link to take user to the default project page
			if (overrides?.navigateOnBackMenuItemClick !== true) {
				ev.preventDefault();
			}
			overrides.BackMenuItem?.onClick?.(ev);
		},
	} as NestingMenuItemProps['overrides']['BackMenuItem'];

	const icon = useMemo(() => {
		if (showIcons !== true) return null;
		return item.iconUrl != null && item.iconUrl.trim().length > 0 ? (
			<ConnectIcon src={item.iconUrl} alt="" />
		) : (
			<AddonIcon spacing="spacious" label="" />
		);
	}, [showIcons, item.iconUrl]);

	const onItemClick = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			// We need to check we're clicking an item inside a nested menu for a connect item.
			// if we are deep linking to a nested connect item -> we need to maintain the hiding of nav-modules
			// @ts-expect-error TS2339 | TS2339 - Property 'ParentMenuItem' does not exist on type 'Overrides & { CustomItem?: { render: (props: CustomItemProps<CustomItemComponentProps>) => ReactElement<any, string | JSXElementConstructor<any>>; } | undefined; } & { ...; }'. | Property 'ParentMenuItem' does not exist on type 'Overrides & { CustomItem?: { render: (props: CustomItemProps<CustomItemComponentProps>) => ReactElement<any, string | JSXElementConstructor<any>>; } | undefined; } & { ...; }'.
			if (overrides?.ParentMenuItem?.onClick && isNested) {
				// @ts-expect-error TS2339 | TS2339 - Property 'ParentMenuItem' does not exist on type 'Overrides & { CustomItem?: { render: (props: CustomItemProps<CustomItemComponentProps>) => ReactElement<any, string | JSXElementConstructor<any>>; } | undefined; } & { ...; }'. | Property 'ParentMenuItem' does not exist on type 'Overrides & { CustomItem?: { render: (props: CustomItemProps<CustomItemComponentProps>) => ReactElement<any, string | JSXElementConstructor<any>>; } | undefined; } & { ...; }'.
				overrides.ParentMenuItem.onClick(event);
			}
			if (isDialog) {
				withConnectHost((connectHost) => {
					if (webItemRef.current && item.styleClass && item.url) {
						connectHost.triggerWebItem(webItemRef.current, item.styleClass, item.url);
					}
				});
			} else {
				item.url != null && addNew(item.url);
			}
		},
		[addNew, isDialog, isNested, item.styleClass, item.url, overrides],
	);

	const onNestedMenuItemClick = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			// Due to CMP project settings not being part to the SPA, we have to set / get the state of the
			// Expandable menu items from cookies - this is because the page does a complete refresh upon clicking a menuitem link.
			// This is to satisfy a requirement for open expandable menus to remain open
			// when users return to the project settings menu
			if (submenuId != null) {
				const cookieData = getCookie(`jira.navigation.expandableMenuStates.${submenuId}`);
				if (cookieData != null) {
					setStrictlyNecessaryCookie(
						`jira.navigation.expandableMenuStates.${submenuId}`,
						JSON.stringify({ ...JSON.parse(cookieData), submenuActive: true }),
					);
				}
			}

			// @ts-expect-error TS2339 | TS2339 - Property 'ParentMenuItem' does not exist on type 'Overrides & { CustomItem?: { render: (props: CustomItemProps<CustomItemComponentProps>) => ReactElement<any, string | JSXElementConstructor<any>>; } | undefined; } & { ...; }'. | Property 'ParentMenuItem' does not exist on type 'Overrides & { CustomItem?: { render: (props: CustomItemProps<CustomItemComponentProps>) => ReactElement<any, string | JSXElementConstructor<any>>; } | undefined; } & { ...; }'.
			if (overrides?.ParentMenuItem?.onClick) {
				// @ts-expect-error TS2339 | TS2339 - Property 'ParentMenuItem' does not exist on type 'Overrides & { CustomItem?: { render: (props: CustomItemProps<CustomItemComponentProps>) => ReactElement<any, string | JSXElementConstructor<any>>; } | undefined; } & { ...; }'. | Property 'ParentMenuItem' does not exist on type 'Overrides & { CustomItem?: { render: (props: CustomItemProps<CustomItemComponentProps>) => ReactElement<any, string | JSXElementConstructor<any>>; } | undefined; } & { ...; }'.
				overrides.ParentMenuItem.onClick(event);
			}
			return () => null;
		},
		[overrides, submenuId],
	);

	if (item.section === true) {
		return (
			<Section title={item.name} hasSeparator={Boolean(index)}>
				{(item.items || []).map((si, idx) => (
					<ConnectItem
						key={si.id}
						item={si}
						overrides={{
							...overrides,
							BackMenuItem,
						}}
						index={idx}
						isNested={isNested}
					/>
				))}
			</Section>
		);
	}

	if (item.items) {
		return (
			<NestingMenuItem
				id={item.id}
				analytics={{ itemId: item.id }}
				iconBefore={icon}
				overrides={{
					...overrides,
					BackMenuItem,
				}}
				title={item.name}
				onClick={onNestedMenuItemClick}
			>
				<NavigationContentWrapper submenuActive nestedMenuOpen>
					<Section>
						<TitleHeader>{item.name}</TitleHeader>
						{(item.items || []).map((si, nestIndex) => (
							<ConnectItem
								key={si.id}
								item={si}
								index={nestIndex}
								overrides={{
									...overrides,
									BackMenuItem: {
										href: item.url,
										title: si.name,
										onClick: (ev) => ev.preventDefault(),
									},
								}}
								isNested
							/>
						))}
					</Section>
				</NavigationContentWrapper>
			</NestingMenuItem>
		);
	}

	return (
		<MenuItem
			analytics={{ itemId: item.id }}
			selectedOn={matchUrlExact()}
			iconBefore={icon}
			href={isDialog ? undefined : item.url}
			onClick={onItemClick}
			ref={webItemRef}
		>
			{item.name}
		</MenuItem>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConnectIcon = styled.img({
	width: '20px',
	height: '20px',
});
